import React from 'react';

const ROWS = [
  ['Dimensions', ['dimension', 'Dimension', 'Dimensions']],
  ['Cut-out', ['cut_out', 'Cut Out', 'Cut-out']],
  ['Power', ['power', 'Power', 'wattage', 'Wattage', 'power_range']],
  ['Lumen output', ['lumen_output', 'Lumen Output', 'Luminous Flux']],
  ['Efficacy', ['__efficacy']],
  ['Optics / beam angle', ['beam_angle', 'Beam Angle', 'optics', 'Optics', 'beam_angle_range']],
  ['Colour temperature', ['color_temp', 'color_temperature', 'Colour Temperature', 'Color Temperature', 'CCT', 'cct_values']],
  ['CRI Ra', ['cri', 'CRI', 'Colour Rendering', 'cri_range']],
  ['R9', ['r9']],
  ['TM-30 Rf / Rg', ['tm30_rf', 'tm30_rg']],
  ['UGR', ['ugr_rating', 'UGR']],
  ['IP rating', ['ip_rating', 'IP Rating', 'ip_rating_normalized', 'ip_rating_options']],
  ['IK rating', ['ik_rating', 'IK Rating']],
  ['Ambient temperature (Ta)', ['working_temp', 'Working Temperature', 'Ambient Temperature']],
  ['Driver', ['driver', 'Driver', 'driver_type']],
  ['Dimming / control', ['dimming_function', 'Dimming', 'control_type', 'Control Type']],
  ['Input voltage', ['input_voltage', 'Input Voltage', 'input_voltage_normalized']],
  ['Light source', ['light_source', 'Light Source']],
  ['Material', ['material', 'Material', 'cover_material', 'Cover Material']],
  ['Finish', ['body_finish', 'Body Finish', 'Finish', 'body_finish_normalized', 'body_finish_options']],
  ['Mounting / installation', ['mounting', 'Mounting', 'installation', 'Installation']],
  ['Net weight', ['weight', 'Net Weight']],
  ['Certification', ['certification', 'Certification', 'Approval']],
  ['Lifetime', ['life_time', 'LED Life Time', 'Lifetime']],
  ['Warranty', ['warranty', 'Warranty']],
  ['Lead time', ['lead_time', 'Lead Time']],
];

const clean = (v) => (v === null || v === undefined ? '' : String(v).trim().replace(/aluminum/ig, 'Aluminium'));

// The index stores both unit-carrying values ("10W", "3000K") and bare numerics
// ("10", "3000"); show one readable set instead of both.
const UNIT = { Power: 'W', 'Colour temperature': 'K', 'Optics / beam angle': '°', 'CRI / R9': '' };

function tidy(label, values) {
  const unit = UNIT[label];
  let out = values.map((v) => (unit && /^\d+(\.\d+)?$/.test(v) ? `${v}${unit}` : v));
  const seen = new Map();
  out.forEach((v) => {
    const key = v.toLowerCase().replace(/\s+/g, '');
    if (!seen.has(key)) seen.set(key, v);
  });
  out = [...seen.values()];
  // a bare number is redundant when the same number appears with a unit
  return out.filter((v) => !(/^\d+(\.\d+)?$/.test(v) && out.some((o) => o !== v && o.startsWith(v))));
}

function sourcesOf(parent, articles) {
  const out = [parent];
  articles.forEach((a) => {
    out.push(a);
    // an article row can hold its SKUs directly, or nested variant rows that do
    const rows = [a, ...(a.variants || [])];
    rows.forEach((row) => {
      (row.skus || []).slice(0, 60).forEach((sku) => out.push(sku));
      if (row.base_specifications) out.push(row.base_specifications);
      if (row !== a) out.push(row);
    });
  });
  return out.filter(Boolean);
}

function collect(rowKeys, parent, articles) {
  const values = new Set();
  sourcesOf(parent, articles).forEach((src) => {
    if (!src) return;
    rowKeys.forEach((key) => {
      let raw = src[key];
      if (key === 'lumen_output' && isEfficacyValue(raw)) raw = null;
      if (Array.isArray(raw)) {
        raw.forEach((v) => { const c = clean(v); if (c) values.add(c); });
        return;
      }
      const direct = clean(raw);
      if (direct) values.add(direct);
    });
    (src.spec_sheet || []).forEach((row) => {
      const label = clean(row.label);
      if (rowKeys.some((k) => k.toLowerCase() === label.toLowerCase())) {
        const val = clean(row.value);
        if (val) values.add(val);
      }
    });
  });
  return [...values];
}

// some masters store an efficacy figure (lm/W) in the lumen field — it is not
// a luminous flux value and must not be divided by the wattage again
const isEfficacyValue = (v) => /lm\s*\/\s*w/i.test(String(v || ''));

function efficacy(parent, articles) {
  const out = new Set();
  sourcesOf(parent, articles).forEach((src) => {
    if (!src) return;
    if (isEfficacyValue(src.lumen_output)) {
      out.add(String(src.lumen_output).trim());
      return;
    }
    // Calculation needs an approved system-output basis, never a family range.
    if (src.efficacy && src.efficacy_evidence) out.add(String(src.efficacy));
  });
  return [...out];
}

/**
 * The full specification table the 2026 audit asks a family page to carry.
 * Values come from the family's own records; anything the master does not
 * supply is shown as "On request" rather than invented.
 */
export function FamilySpecTable({ parent, articles = [] }) {
  const rows = ROWS.map(([label, keys]) => {
    const raw = keys[0] === '__efficacy' ? efficacy(parent, articles) : collect(keys, parent, articles);
    return { label, values: tidy(label, raw) };
  });
  const populated = rows.filter((r) => r.values.length);
  if (!populated.length) return null;

  return (
    <div data-testid="family-full-spec-table">
      <table className="w-full text-sm border-collapse">
        <tbody>
          {rows.map(({ label, values }) => (
            <tr key={label} className="border-b border-[#EDEDED] align-top">
              <th scope="row" className="py-3 pr-6 text-left font-medium text-[#555] w-[44%] md:w-[30%]">
                {label}
              </th>
              <td className="py-3 text-[#111]" data-testid={`spec-row-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                {values.length ? (
                  values.length > 4 ? `${values.slice(0, 4).join(' · ')} +${values.length - 4} more` : values.join(' · ')
                ) : (
                  <span className="text-[#666]">Not published — technical review required</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-4 text-xs text-[#888]">
        {populated.length} of {rows.length} specification rows published from the 2026 master data.
        Values vary by model; missing test data is not estimated. Select an exact model for approval.
      </p>
    </div>
  );
}

export default FamilySpecTable;
